import React, { useEffect, useState, ReactNode } from 'react';
import { Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  CircleAlert,
  IdCard,
  XCircle,
} from 'lucide-react';
import { MatriculaRequest } from 'types/Matriculas';

interface Props {
  isLoading: boolean;
  matriculacion: Partial<MatriculaRequest>;
}

export default function HomeMatriculacion({
  isLoading,
  matriculacion,
}: Props) {
  const [estadoName, setEstadoName] = useState<string>('');
  const [mensaje, setMensaje] = useState<string>('');
  const [linkTo, setLinkTo] = useState<string>('/matriculacion');
  const [linkText, setLinkText] = useState<string>('');
  const [estadoIcon, setEstadoIcon] = useState<ReactNode | null>(null);
  const [estadoColor, setEstadoColor] = useState<string>('');
  const [estadoColorOscuro, serEstadoColorOscuro] = useState<string>('');

  const history = useHistory();

  const handleAccion = () => {
    if (linkTo === '/payment-jura') {
      history.push(linkTo, {
        matriculacion,
        turnoSelected: matriculacion.turno_jura_id,
        fecha: matriculacion.tj_fecha_hora?.split(' ')[0],
        hora: matriculacion.tj_fecha_hora?.split(' ')[1],
        lugar: matriculacion.tj_lugar,
      });
    } else {
      history.push(linkTo, { matriculacion });
    }
  };

  useEffect(() => {
    switch (matriculacion.state_id) {
      case 1:
        setEstadoName('Evaluación en curso');
        setMensaje('La evaluación se completará en los próximos días hábiles.');
        setLinkTo('');
        setLinkText('');
        setEstadoIcon(<CircleAlert size={16} stroke="currentColor" className="me-1" />);
        setEstadoColor('#D9F5FD');
        serEstadoColorOscuro('#1D70B8');
        break;
      case 2:
        setEstadoName('Aprobada');
        setEstadoIcon(<CheckCircle size={16} stroke="currentColor" className="me-1" />);
        setEstadoColor('#DFFFD9');
        serEstadoColorOscuro('#1E6D0E');
        if (!!matriculacion.payment_id && !!matriculacion.turno_jura_id) {
          setLinkTo('/matriculacion/pago-exitoso');
          setLinkText('VER RESERVA');
          setMensaje('Tu reserva para la jura de tu matrícula se realizó con éxito');
        } else if (!!matriculacion.turno_jura_id) {
          setLinkTo('/payment-jura');
          setLinkText('REALIZAR PAGO');
          setMensaje('Tu reserva para la jura de tu matrícula se realizó con éxito.' +
            ' Ya podés abonar la misma');
        } else {
          setLinkTo('/programar-fecha');
          setLinkText('PROGRAMAR FECHA');
          setMensaje(
            'Ya podés reservar la fecha para realizar la jura de ' +
            'tu matrícula de forma presencial y abonar la misma');
        }
        break;
      case 3:
        setEstadoName('Solicitud rechazada');
        setLinkTo('/matriculacion');
        setLinkText('REVISAR');
        setEstadoIcon(<XCircle size={16} stroke="currentColor" className="me-1" />);
        setEstadoColor('#FFC9CD');
        serEstadoColorOscuro('#CE0311');
        break;
    }
  }, [matriculacion]);

  return (
    <div className="d-flex justify-content-between align-items-center">
      {/* Izquierda: icono + título (una sola línea) */}
      <div className="d-flex align-items-center flex-shrink-0">
        <div
          style={{
            backgroundColor: '#F8E9FF',
            padding: 10,
            borderRadius: 30,
          }}
        >
          <IdCard
            color="#a13bd0"
            size={24}
            className=" text-secondary"
          />
        </div>

        <strong
          className="text-truncate"
          style={{
            maxWidth: '447px',
            marginLeft: 5,
            fontSize: '22px',
            fontFamily: 'Roboto',
            fontWeight: 500,
          }}
        >
          Solicitud de Matriculación
        </strong>
      </div>

      {/* Derecha */}
      <div className="d-flex align-items-center gap-3">
        { !!matriculacion.state_id
          ?
            <>
              { !!matriculacion.matriculate_id
                ?
                  <span
                    className="text-muted"
                    style={{
                      maxWidth: '400px',
                      fontSize: '0.9rem',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      marginRight: '20px',
                    }}
                  >
                    Tu matrícula fue creada con éxito. MP: <b>{matriculacion.matriculate_id}</b>
                  </span>
                :
                  <>
                    <span
                      className="d-inline-flex align-items-center px-2 py-1"
                      style={{
                        border: `1px solid ${estadoColorOscuro}`,
                        backgroundColor: `${estadoColor}`,
                        color: `${estadoColorOscuro}`,
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        borderRadius: '30px',
                      }}
                    >
                      {estadoIcon}
                      <span
                        style={{ color: `${estadoColorOscuro}` }}
                        className="ms-1"
                      >
                        {estadoName}
                      </span>
                    </span>
                    <span
                      className="text-muted"
                      style={{
                        maxWidth: '400px',
                        fontSize: '0.9rem',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {mensaje}
                    </span>
                    {
                      !isLoading && (matriculacion.state_id === 2 || matriculacion.state_id === 3)
                        ?
                          <Button
                            type="button"
                            className="
                              btn btn-secondary
                              text-uppercase
                              text-primary
                              d-flex
                              align-items-center
                            "
                            style={{ textDecoration: 'underline', backgroundColor: '#FFFFFF' }}
                            onClick={() => handleAccion()}
                          >
                            {linkText}
                            <ArrowRight size={16} className="ms-1" />
                          </Button>
                        : null
                    }
                  </>
              }
            </>
          :
            <Button
              type="button"
              className="btn btn-secondary btn-iniciar bg-transparent fw-bold d-flex align-items-center"
              style={{
                width: '188px',
                height: '35px',
                border: '1px solid #9123C0',
                color: '#9123C0',
                fontSize: '15px',
                justifyContent: 'center',
              }}
              onClick={() => handleAccion()}
            >
              INICIAR
              <ArrowRight size={16} className="ms-1" />
            </Button>
        }
      </div>
    </div>
  );
}
